import { useState, useContext } from "react";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import styles from "./styles.module.css";
import { ROUTES, SYSTEM_VIEW } from "const";
import ExpensesDialog from "../../popups/ExpensesDialog/ExpensesDialog";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as ActiveOrderIcon } from "images/svg/copy.svg";
import { ReactComponent as CustomerIcon } from "images/svg/customer.svg";
import { ReactComponent as MenuIcon } from "images/svg/menu.svg";
import { ReactComponent as ReportIcon } from "images/svg/report.svg";
import { ReactComponent as SyncIcon } from "images/svg/sync.svg";
import { ReactComponent as WalletIcon } from "images/svg/wallet.svg";
import { ReactComponent as TableIcon } from "images/svg/slider-vertical.svg";
import { ReactComponent as SettingsIcon } from "images/svg/setting.svg";
import { ReactComponent as StockDamageIcon } from "images/svg/stock-damage.svg";
import { ReactComponent as VendorIcon } from "images/svg/truck.svg";
import { ReactComponent as CartIcon } from "images/svg/shopping-cart.svg";
import { ReactComponent as CashBoxIcon } from "images/svg/cashbox.svg";
import { Menu, MenuItem } from "@mui/material";
import * as ERPHlper from "helpers/ERPHelper";
import { TerminalContext } from "context/TerminalContext";
import SyncDialog from "../../popups/SyncDialog/SyncDialog";
import ShopContext from "context/ShopContext";
import validityExpired from "components/utils/validityExpired";
import ShopsHelper from "helpers/ShopsHelper";
import SalesPersonContext from "context/SalesPersonContext";
import * as TerminalHelper from "helpers/TermialHelper";
import * as InvoiceHelper from "helpers/InvoiceHelper";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const TabItem = styled(Tab)(({ theme }) => ({
  textTransform: "initial",
  margin: theme.spacing(0, 1),
  minHeight: 0,
  minWidth: "max-content",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  fontSize: "14px",
  color: "#2B2B2B",
  borderRadius: "8px",
  "&:hover": { backgroundColor: "#eeeeee" },
  "&.Mui-selected": {
    color: "rgba(229, 2, 24, 1)",
    "& svg": { stroke: "rgba(229, 2, 24, 1)  !important" },
  },
}));

const StyledTabs = styled((props) => (
  <Tabs {...props} TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }} />
))({
  display: "flex",
  alignItems: "center",
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiTab-iconWrapper": {
    marginRight: 0,
  },
});

function NavbarActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const [addExpense, setAddExpense] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSyncOpen, setIsSyncOpen] = useState(false);
  const isMoreOpen = Boolean(anchorEl);
  const { tableDashboard } = useContext(TerminalContext);
  const { selectedShop } = useContext(ShopContext);
  const { currentSalesperson, clearSalespersonSession } = useContext(SalesPersonContext);

  const terminalSettings = TerminalHelper.getTerminalSettings();
  const isSalespersonLoginRequired = !!terminalSettings?.salespersonLogin;

  const isPremium = ShopsHelper.isPremiumShop(selectedShop);
  const hasCustomerSupport = !validityExpired(selectedShop?.customer_support_validity);
  const hasOnlineOrderSupport = !validityExpired(selectedShop?.online_cart_validity);

  const handleMoreClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const systemView = ERPHlper.getSystemView();

  const handleSyncData = () => {
    handleMoreClose();
    setIsSyncOpen(true);
  };

  const tabs = [];

  tabs.push({ route: ROUTES.ACTIVE_ORDERS, label: "Active Orders", icon: <ActiveOrderIcon /> });

  if (tableDashboard) {
    tabs.push({ route: ROUTES.TABLE_DASHBOARD, label: "Table Dashboard", icon: <TableIcon /> });
  }

  tabs.push({ route: ROUTES.REPORT, label: "Reports", icon: <ReportIcon /> });
  tabs.push({ route: "more", label: "More", icon: <MenuIcon />, isMore: true });

  let activeTabValue = location.pathname;

  const moreRoutes = [
    ROUTES.ONLINE_ORDERS,
    ROUTES.EXPENSE,
    ROUTES.SYNC,
    ROUTES.ERP_DASHBOARD,
    ROUTES.STOCK_DAMAGES,
    ROUTES.PURCHASE,
    ROUTES.VENDORS,
    ROUTES.PRODUCTS,
    ROUTES.MANAGE,
  ];

  if (systemView !== SYSTEM_VIEW.ERP) {
    moreRoutes.push(ROUTES.CUSTOMERS);
  }

  if (
    moreRoutes.includes(location.pathname) ||
    location.pathname.startsWith(ROUTES.MANAGE) ||
    location.pathname.startsWith(ROUTES.PRODUCTS)
  ) {
    activeTabValue = "more";
  }

  // Ensure activeTabValue is valid (matches one of the TabItem values)
  const validTabValues = [
    ROUTES.ACTIVE_ORDERS,
    tableDashboard && ROUTES.TABLE_DASHBOARD,
    ROUTES.REPORT,
    systemView === SYSTEM_VIEW.ERP && hasCustomerSupport && ROUTES.CUSTOMERS,
    "more",
  ].filter(Boolean);

  if (!validTabValues.includes(activeTabValue)) {
    activeTabValue = false;
  }

  const handleAddExpense = () => {
    setAddExpense(true);
  };

  return (
    <>
      <StyledTabs value={activeTabValue} className={styles.wrapper}>
        <TabItem
          value={ROUTES.ACTIVE_ORDERS}
          icon={<ActiveOrderIcon className={styles.icon} />}
          iconPosition="start"
          label="Active Orders"
          onClick={() => navigate(ROUTES.ACTIVE_ORDERS)}
        />
        {tableDashboard && (
          <TabItem
            value={ROUTES.TABLE_DASHBOARD}
            icon={<TableIcon className={styles.icon} />}
            iconPosition="start"
            label="Table Dashboard"
            onClick={() => navigate(ROUTES.TABLE_DASHBOARD)}
          />
        )}

        {hasOnlineOrderSupport && (
          <TabItem
            value={ROUTES.ONLINE_ORDERS}
            icon={<CartIcon className={styles.icon} />}
            iconPosition="start"
            label="Online Orders"
            onClick={() => navigate(ROUTES.ONLINE_ORDERS)}
          />
        )}

        <TabItem
          value={ROUTES.REPORT}
          icon={<ReportIcon className={styles.icon} />}
          iconPosition="start"
          label="Reports"
          onClick={() => navigate(ROUTES.REPORT)}
        />

        {systemView === SYSTEM_VIEW.ERP && hasCustomerSupport && (
          <TabItem
            value={ROUTES.CUSTOMERS}
            icon={<CustomerIcon className={styles.icon} />}
            iconPosition="start"
            label="Customers"
            onClick={() => navigate(ROUTES.CUSTOMERS)}
          />
        )}
        <TabItem
          value="more"
          icon={<MenuIcon className={styles.icon} />}
          iconPosition="start"
          label="More"
          onClick={handleMoreClick}
        />
      </StyledTabs>
      <Menu
        anchorEl={anchorEl}
        open={isMoreOpen}
        onClose={handleMoreClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        disablePortal={false}
        disableAutoFocusItem={true}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              overflow: "visible",
              borderRadius: "12px",
              border: "1px solid #cdcdcf",
              padding: 0,
              minWidth: 140,
              "& .MuiMenuItem-root": {
                height: "50px",
                fontSize: "14px",
                fontWeight: 600,
                margin: "0 10px",
                padding: "5px",
                gap: "5px",
                borderBottom: "1px solid #E6E4E9",
                "&:last-child": {
                  borderBottom: "none",
                },
              },
              "& .MuiMenu-list": {
                padding: 0,
              },
              "&:before": {
                content: '""',
                position: "absolute",
                top: -1,
                right: 20,
                width: 12,
                height: 12,
                background: "white",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
                boxShadow: "-2px -2px 2px rgba(0,0,0,0.05)",
                borderTop: "1px solid #cdcdcf",
                borderLeft: "1px solid #cdcdcf",
              },
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleSyncData();
          }}
        >
          <SyncIcon className={styles.icon} />
          Sync
        </MenuItem>

        {isPremium && (
          <MenuItem
            onClick={() => {
              handleAddExpense();
              handleMoreClose();
            }}
          >
            <WalletIcon className={styles.icon} />
            Expense
          </MenuItem>
        )}

        {hasCustomerSupport && systemView !== SYSTEM_VIEW.ERP && (
          <MenuItem
            onClick={() => {
              navigate(ROUTES.CUSTOMERS);
              handleMoreClose();
            }}
          >
            <CustomerIcon className={styles.icon} />
            Customers
          </MenuItem>
        )}

        {hasCustomerSupport && (
          <MenuItem
            onClick={() => {
              navigate(ROUTES.VENDORS);
              handleMoreClose();
            }}
          >
            <VendorIcon className={styles.icon} />
            Vendors
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            navigate(ROUTES.PURCHASE);
            handleMoreClose();
          }}
        >
          <CartIcon className={styles.icon} />
          Purchase
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(ROUTES.STOCK_DAMAGES);
            handleMoreClose();
          }}
        >
          <StockDamageIcon className={styles.icon} />
          Stock Damages
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (window.innerWidth <= 768) {
              navigate(ROUTES.MANAGE);
            } else {
              navigate(ROUTES.PRODUCTS);
            }
            handleMoreClose();
          }}
        >
          <SettingsIcon className={styles.icon} />
          Manage
        </MenuItem>

        <MenuItem
          onClick={() => {
            InvoiceHelper.openCashDrawer();
            handleMoreClose();
          }}
        >
          <CashBoxIcon className={styles.icon} />
          Cash Box
        </MenuItem>

        {isSalespersonLoginRequired && currentSalesperson && (
          <MenuItem
            onClick={() => {
              handleMoreClose();
              clearSalespersonSession();
              navigate(ROUTES.LOCK_SCREEN);
            }}
          >
            <LockOutlinedIcon className={styles.icon} />
            Lock
          </MenuItem>
        )}
      </Menu>

      {addExpense && <ExpensesDialog handleClose={() => setAddExpense(false)} />}
      <SyncDialog open={isSyncOpen} onClose={() => setIsSyncOpen(false)} />
    </>
  );
}

export default NavbarActions;
